/**
 * Dialog for uploading subjects and attributes from Excel/CSV files.
 * Parses files client-side using the xlsx library, shows a preview,
 * auto-detects column mappings, and calls the UAS bulk import endpoint.
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  X,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react'
import ExcelJS from 'exceljs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useSubjectUpload } from '@/hooks/use-subject-upload'
import type { BulkImportItem, BulkImportResult } from '@/hooks/use-subject-upload'
import { TEMPLATE_COLUMNS, downloadSubjectTemplate } from '@/lib/subject-template'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SubjectUploadDialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

interface ColumnMapping {
  readonly identifier: number | null
  readonly identifierType: 'EMAIL' | 'EMPLOYEE_ID' | 'USERNAME'
  readonly displayName: number | null
  readonly role: number | null
  readonly department: number | null
  readonly clearance: number | null
}

interface ParsedData {
  readonly headers: ReadonlyArray<string>
  readonly rows: ReadonlyArray<ReadonlyArray<string>>
  readonly mapping: ColumnMapping
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCEPTED_EXTENSIONS = '.xlsx,.csv'
const MAX_FILE_SIZE = 10 * 1024 * 1024
const PREVIEW_ROW_COUNT = 5

/**
 * Column name patterns for auto-detection (case-insensitive matching).
 * Each key maps to an array of patterns to search for in the header row.
 */
const COLUMN_PATTERNS: Record<string, ReadonlyArray<string>> = {
  identifier: [
    'user id',
    'userid',
    'email',
    'employee id',
    'employeeid',
    'employee_id',
    'username',
    'user_id',
    'subject',
    'subject_id',
    'id',
  ],
  displayName: [
    'display name',
    'displayname',
    'display_name',
    'name',
    'full name',
    'fullname',
    'full_name',
  ],
  role: ['role', 'roles', 'job role', 'job_role', 'position', 'title', 'job title', 'job_title'],
  department: [
    'department',
    'dept',
    'division',
    'team',
    'group',
    'org',
    'organization',
    'business_unit',
    'business unit',
  ],
  clearance: [
    'clearance',
    'clearance level',
    'clearance_level',
    'security clearance',
    'security_clearance',
    'access level',
    'access_level',
  ],
}

/**
 * Patterns that specifically indicate the identifier column holds an email address.
 */
const EMAIL_PATTERNS: ReadonlyArray<string> = ['email', 'e-mail', 'email address', 'email_address']

/**
 * Patterns that specifically indicate the identifier column holds an employee ID.
 */
const EMPLOYEE_ID_PATTERNS: ReadonlyArray<string> = [
  'employee id',
  'employeeid',
  'employee_id',
  'emp id',
  'emp_id',
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFileIcon(filename: string) {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'))
  if (ext === '.xlsx') return FileSpreadsheet
  return FileText
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function findColumnIndex(
  headers: ReadonlyArray<string>,
  patterns: ReadonlyArray<string>,
): number | null {
  const normalised = headers.map((h) => h.trim().toLowerCase())
  for (const pattern of patterns) {
    const idx = normalised.indexOf(pattern)
    if (idx !== -1) return idx
  }
  return null
}

function detectIdentifierType(
  headers: ReadonlyArray<string>,
  identifierIndex: number | null,
): 'EMAIL' | 'EMPLOYEE_ID' | 'USERNAME' {
  if (identifierIndex === null) return 'EMAIL'
  const header = headers[identifierIndex]?.trim().toLowerCase() ?? ''

  for (const p of EMAIL_PATTERNS) {
    if (header === p) return 'EMAIL'
  }
  for (const p of EMPLOYEE_ID_PATTERNS) {
    if (header === p) return 'EMPLOYEE_ID'
  }
  return 'EMAIL'
}

function autoDetectMapping(headers: ReadonlyArray<string>): ColumnMapping {
  const identifier = findColumnIndex(headers, COLUMN_PATTERNS.identifier)
  const identifierType = detectIdentifierType(headers, identifier)
  return {
    identifier,
    identifierType,
    displayName: findColumnIndex(headers, COLUMN_PATTERNS.displayName),
    role: findColumnIndex(headers, COLUMN_PATTERNS.role),
    department: findColumnIndex(headers, COLUMN_PATTERNS.department),
    clearance: findColumnIndex(headers, COLUMN_PATTERNS.clearance),
  }
}

function cellToString(cell: unknown): string {
  if (cell === null || cell === undefined) return ''
  return String(cell).trim()
}

function parseCsvText(text: string): ParsedData {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  if (lines.length < 2) {
    throw new Error('File must contain a header row and at least one data row')
  }

  const headers: ReadonlyArray<string> = lines[0].split(',').map((h) => h.trim())
  const rows: ReadonlyArray<ReadonlyArray<string>> = lines
    .slice(1)
    .map((line) => line.split(',').map((c) => c.trim()))
    .filter((row) => row.some((c) => c !== ''))

  if (rows.length === 0) {
    throw new Error('No data rows found in file')
  }

  const mapping = autoDetectMapping(headers)
  return { headers, rows, mapping }
}

function parseLoadedWorkbook(workbook: ExcelJS.Workbook): ParsedData {
  const sheet = workbook.worksheets[0]
  if (!sheet || sheet.rowCount === 0) {
    throw new Error('The workbook contains no sheets')
  }

  const rawRows: Array<Array<unknown>> = []
  sheet.eachRow((row) => {
    rawRows.push(row.values as Array<unknown>)
  })

  if (rawRows.length < 2) {
    throw new Error('File must contain a header row and at least one data row')
  }

  // ExcelJS row.values is 1-indexed (index 0 is undefined), so slice from 1
  const headerRaw = rawRows[0] as Array<unknown>
  const headers: ReadonlyArray<string> = headerRaw.slice(1).map(cellToString)

  const rows: ReadonlyArray<ReadonlyArray<string>> = rawRows
    .slice(1)
    .map((row) => (row as Array<unknown>).slice(1).map(cellToString))
    .filter((row) => row.some((c) => c !== ''))

  if (rows.length === 0) {
    throw new Error('No data rows found in file')
  }

  const mapping = autoDetectMapping(headers)
  return { headers, rows, mapping }
}

function buildImportItems(parsed: ParsedData): ReadonlyArray<BulkImportItem> {
  const { rows, mapping } = parsed

  if (mapping.identifier === null) {
    throw new Error('Could not detect an identifier column (User ID, Email, Employee ID)')
  }

  return rows
    .filter((row) => {
      const id = row[mapping.identifier!] ?? ''
      return id !== ''
    })
    .map((row) => {
      const attributes: Array<{
        readonly attributeKey: string
        readonly value: string
        readonly dataType: 'STRING'
      }> = []

      if (mapping.displayName !== null && row[mapping.displayName]) {
        attributes.push({
          attributeKey: 'displayName',
          value: row[mapping.displayName],
          dataType: 'STRING',
        })
      }

      if (mapping.role !== null && row[mapping.role]) {
        attributes.push({
          attributeKey: 'role',
          value: row[mapping.role],
          dataType: 'STRING',
        })
      }

      if (mapping.department !== null && row[mapping.department]) {
        attributes.push({
          attributeKey: 'department',
          value: row[mapping.department],
          dataType: 'STRING',
        })
      }

      if (mapping.clearance !== null && row[mapping.clearance]) {
        attributes.push({
          attributeKey: 'clearance',
          value: row[mapping.clearance],
          dataType: 'STRING',
        })
      }

      return {
        subjectIdentifier: row[mapping.identifier!],
        identifierType: mapping.identifierType,
        attributes,
      }
    })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubjectUploadDialog({ open, onOpenChange }: SubjectUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [parsed, setParsed] = useState<ParsedData | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useSubjectUpload()

  const resetState = useCallback(() => {
    setFile(null)
    setIsDragging(false)
    setParsed(null)
    setParseError(null)
    setResult(null)
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetState()
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetState],
  )

  const validateFile = useCallback((f: File): string | null => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'))
    if (!['.xlsx', '.csv'].includes(ext)) {
      return `Unsupported file type: ${ext}. Accepted: .xlsx, .csv`
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File too large: ${formatFileSize(f.size)}. Maximum: 10 MB`
    }
    return null
  }, [])

  const handleFileSelect = useCallback(
    async (f: File) => {
      const error = validateFile(f)
      if (error) {
        toast.error(error)
        return
      }

      setFile(f)
      setResult(null)
      setParseError(null)
      setParsed(null)

      try {
        const buffer = await f.arrayBuffer()
        const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'))
        let data: ParsedData
        if (ext === '.csv') {
          data = parseCsvText(new TextDecoder().decode(buffer))
        } else {
          const workbook = new ExcelJS.Workbook()
          await workbook.xlsx.load(buffer)
          data = parseLoadedWorkbook(workbook)
        }
        setParsed(data)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to parse file'
        setParseError(message)
      }
    },
    [validateFile],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) {
        void handleFileSelect(droppedFile)
      }
    },
    [handleFileSelect],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) {
        void handleFileSelect(selectedFile)
      }
    },
    [handleFileSelect],
  )

  const handleUpload = useCallback(async () => {
    if (!parsed) return

    try {
      const items = buildImportItems(parsed)
      if (items.length === 0) {
        toast.error('No valid rows to import')
        return
      }

      const importResult = await uploadMutation.mutateAsync({
        items,
        source: 'CSV_IMPORT',
      })
      setResult(importResult)
    } catch {
      // Error is handled by the mutation's onError callback
    }
  }, [parsed, uploadMutation])

  const previewRows = useMemo(() => {
    if (!parsed) return []
    return parsed.rows.slice(0, PREVIEW_ROW_COUNT)
  }, [parsed])

  const mappedColumnNames = useMemo(() => {
    if (!parsed) return []
    const names: Array<{ readonly label: string; readonly column: string }> = []
    const { mapping, headers } = parsed

    if (mapping.identifier !== null) {
      names.push({
        label: `Identifier (${mapping.identifierType})`,
        column: headers[mapping.identifier],
      })
    }
    if (mapping.displayName !== null) {
      names.push({ label: 'Display Name', column: headers[mapping.displayName] })
    }
    if (mapping.role !== null) {
      names.push({ label: 'Role', column: headers[mapping.role] })
    }
    if (mapping.department !== null) {
      names.push({ label: 'Department', column: headers[mapping.department] })
    }
    if (mapping.clearance !== null) {
      names.push({ label: 'Clearance', column: headers[mapping.clearance] })
    }
    return names
  }, [parsed])

  const totalRows = parsed?.rows.length ?? 0
  const FileIcon = file ? getFileIcon(file.name) : Upload

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload User Attributes</DialogTitle>
          <DialogDescription>
            Import subjects and their attributes from an Excel (.xlsx) or CSV file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          {!result && (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                isDragging && 'border-primary bg-primary/5',
                !isDragging && !file && 'border-muted-foreground/25 hover:border-primary/50',
                file && 'border-primary/50 bg-primary/5',
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleInputChange}
                className="hidden"
              />

              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileIcon className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                      setParsed(null)
                      setParseError(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium">Drop a file here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Supports .xlsx and .csv files (max 10 MB)
                  </p>
                </>
              )}
            </div>
          )}

          {/* Expected columns hint + download template */}
          {!file && !result && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p className="font-medium">Expected columns:</p>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATE_COLUMNS.map((col, i) => (
                  <Badge key={col.header} variant="secondary" className="text-xs">
                    {col.header}{i === 0 ? '*' : ''}
                  </Badge>
                ))}
              </div>
              <p className="text-xs">* At least one identifier column is required.</p>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  void downloadSubjectTemplate('xlsx')
                }}
              >
                <Download className="h-3 w-3 mr-1" />
                Download template (.xlsx)
              </Button>
            </div>
          )}

          {/* Parse error */}
          {parseError && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{parseError}</span>
              </div>
            </div>
          )}

          {/* Column mapping display */}
          {parsed && !result && (
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium mb-2">Detected column mapping:</p>
                <div className="flex flex-wrap gap-2">
                  {mappedColumnNames.length > 0 ? (
                    mappedColumnNames.map((m) => (
                      <Badge key={m.label} variant="outline" className="text-xs">
                        {m.label}: <span className="font-mono ml-1">{m.column}</span>
                      </Badge>
                    ))
                  ) : (
                    <span className="text-destructive text-xs">
                      No columns could be automatically mapped. Please check your column headers.
                    </span>
                  )}
                </div>
              </div>

              {/* Row count */}
              <p className="text-sm text-muted-foreground">
                {totalRows} row{totalRows !== 1 ? 's' : ''} detected
              </p>

              {/* Preview table */}
              {previewRows.length > 0 && (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {parsed.headers.map((h, i) => (
                          <TableHead key={i} className="text-xs whitespace-nowrap">
                            {h}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((row, ri) => (
                        <TableRow key={ri}>
                          {row.map((cell, ci) => (
                            <TableCell key={ci} className="text-xs py-1.5">
                              {cell || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {totalRows > PREVIEW_ROW_COUNT && (
                    <div className="px-3 py-1.5 text-xs text-muted-foreground border-t bg-muted/30">
                      Showing {PREVIEW_ROW_COUNT} of {totalRows} rows
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Upload result */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-1.5 text-sm">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>{result.totalCreated} subject(s) created</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <span>{result.totalUpdated} attribute(s) set</span>
                </div>
                {result.errorCount > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span>{result.errorCount} error(s)</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleUpload()}
                disabled={!parsed || parsed.mapping.identifier === null || uploadMutation.isPending}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    Upload {totalRows > 0 ? `${totalRows} row${totalRows !== 1 ? 's' : ''}` : ''}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
